import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library";
import { Camera, ExternalLink, Image as ImageIcon, Keyboard, RefreshCw, ScanLine, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Live camera scanner with real getUserMedia flow, progressive fallback,
 * iframe/HTTPS diagnostics and native-capture fallback for iOS/Android.
 *
 * Callbacks:
 *  - onDetected({text, format})   → live barcode/QR result
 *  - onCapturePhoto(dataUrl)      → snapshot taken from the live stream (OCR)
 *  - onNativeImage(dataUrl)       → image chosen via native camera / gallery (OCR)
 *  - onManualEntry(text)          → operator typed the code manually
 *  - onClose()
 */
export default function BarcodeScanner({
  onDetected,
  onCapturePhoto,
  onNativeImage,
  onManualEntry,
  onClose,
}) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const streamRef = useRef(null);

  const [mode, setMode] = useState("live"); // 'live' | 'native' | 'manual' | 'blocked'
  const [status, setStatus] = useState("Preparazione fotocamera…");
  const [errorCode, setErrorCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [devices, setDevices] = useState([]);
  const [deviceIdx, setDeviceIdx] = useState(0);
  const [manualCode, setManualCode] = useState("");
  const [running, setRunning] = useState(false);

  const isSecure = typeof window !== "undefined" ? window.isSecureContext : false;
  const inIframe = typeof window !== "undefined" ? window.self !== window.top : false;
  const hasMediaDevices = typeof navigator !== "undefined" && !!navigator.mediaDevices;
  const hasGetUserMedia = hasMediaDevices && typeof navigator.mediaDevices.getUserMedia === "function";

  const stopStream = useCallback(() => {
    try { readerRef.current?.reset(); } catch (_) { /* no-op */ }
    readerRef.current = null;
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* no-op */ } });
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch (_) { /* no-op */ }
    }
    setRunning(false);
  }, []);

  const mapError = useCallback((e) => {
    const code = e?.name || "Error";
    let msg = e?.message || "Errore fotocamera";
    if (code === "NotAllowedError") msg = "Permesso fotocamera negato. Abilitalo nelle impostazioni del browser.";
    else if (code === "NotFoundError") msg = "Nessuna fotocamera disponibile sul dispositivo.";
    else if (code === "NotReadableError") msg = "La fotocamera è utilizzata da un'altra applicazione o non è disponibile.";
    else if (code === "SecurityError") msg = "La fotocamera richiede una connessione HTTPS e l'apertura dell'app in una scheda autonoma.";
    else if (code === "AbortError") msg = "Avvio interrotto. Riprova.";
    else if (code === "OverconstrainedError") msg = "Vincoli fotocamera non supportati. Riprovo con impostazioni più permissive…";
    return { code, msg };
  }, []);

  const tryConstraints = useCallback(async () => {
    const attempts = [
      { video: { facingMode: { exact: "environment" } }, audio: false },
      { video: { facingMode: { ideal: "environment" } }, audio: false },
      { video: true, audio: false },
    ];
    let lastErr = null;
    for (const c of attempts) {
      try {
        const s = await navigator.mediaDevices.getUserMedia(c);
        return s;
      } catch (e) {
        lastErr = e;
        if (e && e.name !== "OverconstrainedError" && e.name !== "ConstraintNotSatisfiedError") {
          throw e;
        }
      }
    }
    throw lastErr || new Error("Impossibile avviare la fotocamera");
  }, []);

  const startLive = useCallback(async () => {
    if (running) return;
    setErrorCode(""); setErrorMsg("");
    if (!isSecure) {
      setMode("blocked");
      setErrorCode("SecurityError");
      setErrorMsg("La connessione non è HTTPS. Apri l'applicazione in una scheda autonoma e in HTTPS.");
      return;
    }
    if (inIframe) {
      setMode("blocked");
      setErrorCode("IframePermissionBlocked");
      setErrorMsg("La fotocamera non può essere utilizzata nell'anteprima incorporata. Apri l'applicazione in una nuova scheda.");
      return;
    }
    if (!hasGetUserMedia) {
      setMode("native");
      setErrorCode("UnsupportedBrowser");
      setErrorMsg("Il browser non espone getUserMedia. Usa lo scatto nativo o l'inserimento manuale.");
      return;
    }
    try {
      setStatus("Richiesta autorizzazione fotocamera…");
      const stream = await tryConstraints();
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) { stream.getTracks().forEach((t) => t.stop()); return; }
      v.srcObject = stream;
      v.setAttribute("playsinline", "true");
      v.muted = true;
      await new Promise((resolve) => {
        if (v.readyState >= 2) return resolve();
        v.onloadedmetadata = () => resolve();
      });
      try { await v.play(); } catch (_) { /* no-op */ }
      setRunning(true);
      setStatus("Inquadra il codice a barre o il QR code");

      try {
        const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
        setDevices(list);
      } catch (_) { /* no-op */ }

      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      reader.decodeFromVideoElement(v, (result, err) => {
        if (result) {
          const text = result.getText();
          const fmt = result.getBarcodeFormat?.();
          try { reader.reset(); } catch (_) { /* no-op */ }
          onDetected?.({ text, format: fmt || "" });
        }
        if (err && !(err instanceof NotFoundException)) { /* ignore */ }
      });
    } catch (e) {
      const { code, msg } = mapError(e);
      setErrorCode(code); setErrorMsg(msg); setStatus(msg);
      setMode(code === "NotAllowedError" || code === "SecurityError" ? "blocked" : "native");
      // eslint-disable-next-line no-console
      console.warn("[BarcodeScanner]", code, e);
      stopStream();
    }
  }, [running, isSecure, inIframe, hasGetUserMedia, tryConstraints, mapError, onDetected, stopStream]);

  useEffect(() => {
    startLive();
    return () => { stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchCamera = async () => {
    if (devices.length < 2) return;
    const nextIdx = (deviceIdx + 1) % devices.length;
    setDeviceIdx(nextIdx);
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: devices[nextIdx].deviceId } }, audio: false });
      streamRef.current = stream;
      const v = videoRef.current;
      v.srcObject = stream;
      await v.play();
      setRunning(true);
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      reader.decodeFromVideoElement(v, (result) => {
        if (result) {
          try { reader.reset(); } catch (_) { /* no-op */ }
          onDetected?.({ text: result.getText(), format: result.getBarcodeFormat?.() || "" });
        }
      });
    } catch (e) {
      const { code, msg } = mapError(e);
      setErrorCode(code); setErrorMsg(msg); setStatus(msg);
    }
  };

  const takeSnapshot = () => {
    const v = videoRef.current;
    if (!v || !running) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      onCapturePhoto?.(dataUrl);
    } catch (e) {
      setErrorMsg("Impossibile catturare la foto: " + e.message);
    }
  };

  const openInNewTab = () => { window.open(window.location.href, "_blank", "noopener,noreferrer"); };
  const handleClose = () => { stopStream(); onClose?.(); };
  const handleNativePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => { onNativeImage?.(r.result); onClose?.(); };
    r.readAsDataURL(file);
    e.target.value = "";
  };
  const submitManual = () => {
    const v = manualCode.trim();
    if (!v) return;
    onManualEntry?.(v);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col" data-testid="barcode-scanner">
      <div className="flex items-center justify-between text-white p-3 bg-black/80 border-b border-white/10">
        <button data-testid="scanner-close" onClick={handleClose} className="p-2" aria-label="Chiudi">
          <X className="w-6 h-6" />
        </button>
        <div className="text-sm font-semibold">Scansione codice</div>
        <button data-testid="scanner-switch-camera" onClick={switchCamera} className="p-2 disabled:opacity-40" disabled={devices.length < 2 || mode !== "live"} aria-label="Cambia fotocamera">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-3 text-xs text-white bg-black/60 border-b border-white/10">
        <button data-testid="scanner-mode-live" onClick={() => { setMode("live"); startLive(); }} className={`py-2 flex items-center justify-center gap-1 ${mode === "live" ? "text-white bg-white/10" : "text-white/70"}`}>
          <ScanLine className="w-3.5 h-3.5" /> Live
        </button>
        <button data-testid="scanner-mode-native" onClick={() => { stopStream(); setMode("native"); }} className={`py-2 flex items-center justify-center gap-1 ${mode === "native" ? "text-white bg-white/10" : "text-white/70"}`}>
          <Camera className="w-3.5 h-3.5" /> Scatta / carica
        </button>
        <button data-testid="scanner-mode-manual" onClick={() => { stopStream(); setMode("manual"); }} className={`py-2 flex items-center justify-center gap-1 ${mode === "manual" ? "text-white bg-white/10" : "text-white/70"}`}>
          <Keyboard className="w-3.5 h-3.5" /> Manuale
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {mode === "live" && (
          <>
            <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" autoPlay playsInline muted data-testid="scanner-video" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative w-[78%] max-w-[420px] aspect-[4/3]">
                <span className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
                <span className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
                <span className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                <span className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
              </div>
            </div>
            <div className="absolute top-3 left-0 right-0 text-center text-white/90 text-xs px-4">{status}</div>
          </>
        )}

        {mode === "blocked" && (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white">
            <div className="max-w-md space-y-4">
              <div className="text-lg font-heading font-bold">Fotocamera non disponibile</div>
              <div className="text-sm text-white/80">{errorMsg}</div>
              <div className="text-[11px] text-white/60 font-mono">Codice: {errorCode}</div>
              <div className="flex flex-col gap-2 pt-2">
                <Button data-testid="scanner-open-new-tab" onClick={openInNewTab} className="ama-btn-primary">
                  <ExternalLink className="w-4 h-4 mr-2" /> Apri in una nuova scheda
                </Button>
                <Button variant="outline" onClick={() => setMode("native")} className="bg-white/10 text-white border-white/30 hover:bg-white/20">
                  Scatta foto con la fotocamera del dispositivo
                </Button>
                <Button variant="outline" onClick={() => setMode("manual")} className="bg-white/10 text-white border-white/30 hover:bg-white/20">
                  Inserisci manualmente
                </Button>
              </div>
            </div>
          </div>
        )}

        {mode === "native" && (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white">
            <div className="max-w-md space-y-4">
              <Camera className="w-10 h-10 mx-auto text-white/80" />
              <div className="text-lg font-heading font-bold">Fotocamera del dispositivo</div>
              <div className="text-sm text-white/80">
                {"Usa la fotocamera nativa del telefono per fotografare l'etichetta o carica un'immagine esistente. L'immagine sarà analizzata dall'OCR."}
              </div>
              {errorMsg && <div className="text-[11px] text-amber-300">{errorCode ? `${errorCode} — ` : ""}{errorMsg}</div>}
              <div className="flex flex-col gap-2 pt-2">
                <label className="ama-btn-primary rounded-md py-2 px-4 inline-flex items-center justify-center cursor-pointer gap-2">
                  <Camera className="w-4 h-4" /> Scatta foto con la fotocamera
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleNativePick} data-testid="scanner-native-capture" />
                </label>
                <label className="inline-flex items-center justify-center gap-2 cursor-pointer bg-white/10 text-white border border-white/30 rounded-md py-2 px-4 hover:bg-white/20">
                  <ImageIcon className="w-4 h-4" /> Carica dalla galleria
                  <input type="file" accept="image/*" className="hidden" onChange={handleNativePick} data-testid="scanner-gallery-upload" />
                </label>
                <Button variant="outline" onClick={() => setMode("manual")} className="bg-white/10 text-white border-white/30 hover:bg-white/20">
                  <Keyboard className="w-4 h-4 mr-2" /> Inserisci manualmente
                </Button>
              </div>
            </div>
          </div>
        )}

        {mode === "manual" && (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white">
            <div className="max-w-md w-full space-y-4">
              <Keyboard className="w-10 h-10 mx-auto text-white/80" />
              <div className="text-lg font-heading font-bold">Inserimento manuale</div>
              <div className="text-sm text-white/80">Trascrivi manualmente il codice a barre / seriale se non riesci a scansionarlo.</div>
              <input
                data-testid="scanner-manual-input"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
                placeholder="Es. 5FA123456789"
                className="w-full rounded-md bg-white/10 border border-white/30 px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                autoFocus
              />
              <Button data-testid="scanner-manual-submit" onClick={submitManual} disabled={!manualCode.trim()} className="ama-btn-primary w-full">
                Conferma codice
              </Button>
            </div>
          </div>
        )}
      </div>

      {mode === "live" && (
        <div className="p-3 bg-black/80 flex gap-2 justify-center border-t border-white/10">
          <Button data-testid="scanner-capture-photo" onClick={takeSnapshot} disabled={!running} className="ama-btn-primary">
            <Camera className="w-4 h-4 mr-2" /> Fotografa etichetta per OCR
          </Button>
          <Button variant="outline" onClick={() => { stopStream(); setMode("native"); }} className="bg-white/10 text-white border-white/30 hover:bg-white/20">
            Camera dispositivo
          </Button>
        </div>
      )}
    </div>
  );
}
